import { useId } from 'react'
import { Zap } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Which brand mark is live. Every earlier mark is kept, so switching back is
 * a one-word change here and nothing else:
 *  - 'image'       — the supplied artwork, used as-is from public/brand/:
 *                    Setu.svg / Setu-dark.svg (the full lockup, light and
 *                    dark ink) and catalog.svg (the square mark).
 *  - 'bubble-s'    — orange chat-bubble silhouette with a white "S" whose ends
 *                    are two nodes (a conversation that links two people).
 *  - 's-link'      — the same "S" on a rounded-square tile.
 *  - 's-in-bubble' — white chat bubble on the tile, orange "S" inside.
 *  - 'bubbles'     — two overlapping chat bubbles.
 *  - 'bridge'      — arch bridge ("Setu" means bridge).
 *  - 'bolt'        — the first mark, a lightning bolt.
 * Favicons: public/brand/catalog.svg is the live one; public/favicon.svg
 * (s-in-bubble), favicon-bubble-s.svg, favicon-bridge.svg and
 * favicon-bolt.svg are the earlier ones.
 */
export type BrandMark =
  | 'image'
  | 'bubble-s'
  | 's-link'
  | 's-in-bubble'
  | 'bubbles'
  | 'bridge'
  | 'bolt'
export const BRAND_MARK: BrandMark = 'image'

/** Width / height of public/brand/Setu.svg. The lockup is laid out on the
 *  Unnati stacked lockup's grid: mark as tall as the wordmark, "By
 *  avishkruti" right-aligned under it, ink #192732, orange #FD4305. */
const LOCKUP_ASPECT = 215.9 / 88

interface BrandLogoProps {
  /** `full` = mark + wordmark, `mark` = the mark only. */
  variant?: 'full' | 'mark'
  /** Mark edge length in px. */
  size?: number
  /** Show the "WhatsApp CRM" strapline under the wordmark. */
  strapline?: boolean
  /** Override the live mark (used by previews). */
  mark?: BrandMark
  /** `auto` follows the app theme (white ink in dark mode); `light` always
   *  uses the dark-ink lockup, for logos sitting on a white surface. */
  tone?: 'auto' | 'light'
  className?: string
}

/** The supplied artwork, rendered straight from the SVG files. */
function ImageLogo({
  variant,
  size,
  tone,
}: {
  variant: 'full' | 'mark'
  size: number
  tone: 'auto' | 'light'
}) {
  if (variant === 'mark') {
    return (
      <img
        src="/brand/catalog.svg"
        alt="Setu"
        width={size}
        height={size}
        className="shrink-0 object-contain"
      />
    )
  }
  // The lockup already contains the wordmark, so it replaces mark + text.
  // It reads as one wide block, so it is sized taller than the bare mark to
  // fill the sidebar header the way the Unnati lockup does.
  const height = Math.round(size * 1.25)
  const width = Math.round(height * LOCKUP_ASPECT)
  return (
    <>
      <img
        src="/brand/Setu.svg"
        alt="Setu"
        width={width}
        height={height}
        className={cn('shrink-0 object-contain', tone === 'auto' && 'dark:hidden')}
      />
      {tone === 'auto' && (
        <img
          src="/brand/Setu-dark.svg"
          alt=""
          aria-hidden="true"
          width={width}
          height={height}
          className="hidden shrink-0 object-contain dark:block"
        />
      )}
    </>
  )
}

const BUBBLE_TILE =
  'M15 2H33A13 13 0 0 1 46 15V27A13 13 0 0 1 33 40H22L11 47.5V39.4A13 13 0 0 1 2 27V15A13 13 0 0 1 15 2Z'
const INNER_BUBBLE =
  'M17.5 8.5H30.5A9.5 9.5 0 0 1 40 18V26A9.5 9.5 0 0 1 30.5 35.5H23L15.5 41.5V35.3A9.5 9.5 0 0 1 8 26V18A9.5 9.5 0 0 1 17.5 8.5Z'

/** The "S": one stroke joining two nodes. */
function SPath({ color, width, node }: { color: string; width: number; node: number }) {
  return (
    <>
      <path
        d="M31.5 16.2C29.5 11 16 10.6 16 18C16 25 32 23 32 30C32 37.4 18.5 37 16.5 31.8"
        stroke={color}
        strokeWidth={width}
        strokeLinecap="round"
        fill="none"
      />
      <circle cx="31.5" cy="16.2" r={node} fill={color} />
      <circle cx="16.5" cy="31.8" r={node} fill={color} />
    </>
  )
}

function SvgMark({ size, mark }: { size: number; mark: Exclude<BrandMark, 'bolt' | 'image'> }) {
  // unique gradient ids — several logos can be on one page
  const uid = useId().replace(/:/g, '')
  const grad = `setu-g-${uid}`
  const sheen = `setu-s-${uid}`
  const tileShape =
    mark === 'bubble-s' ? (
      <>
        <path d={BUBBLE_TILE} fill={`url(#${grad})`} />
        <path d={BUBBLE_TILE} fill={`url(#${sheen})`} />
      </>
    ) : (
      <>
        <rect width="48" height="48" rx="13" fill={`url(#${grad})`} />
        <rect width="48" height="48" rx="13" fill={`url(#${sheen})`} />
      </>
    )

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      fill="none"
      aria-hidden="true"
      className="shrink-0"
    >
      <defs>
        <linearGradient id={grad} x1="6" y1="2" x2="42" y2="46" gradientUnits="userSpaceOnUse">
          <stop stopColor="#FF7A3D" />
          <stop offset="0.55" stopColor="#FC4305" />
          <stop offset="1" stopColor="#E23800" />
        </linearGradient>
        <radialGradient
          id={sheen}
          cx="0"
          cy="0"
          r="1"
          gradientUnits="userSpaceOnUse"
          gradientTransform="translate(12 8) scale(34)"
        >
          <stop stopColor="#fff" stopOpacity="0.28" />
          <stop offset="1" stopColor="#fff" stopOpacity="0" />
        </radialGradient>
      </defs>
      {tileShape}

      {mark === 'bubble-s' && (
        <g transform="translate(0 -3.2) scale(.93) translate(1.8 1.2)">
          <SPath color="#fff" width={5.2} node={3.7} />
        </g>
      )}

      {mark === 's-link' && <SPath color="#fff" width={5} node={3.6} />}

      {mark === 's-in-bubble' && (
        <>
          <path d={INNER_BUBBLE} fill="#fff" />
          <g transform="translate(24 22) scale(.62) translate(-24 -24)">
            <SPath color="#FC4305" width={5.6} node={4} />
          </g>
        </>
      )}

      {mark === 'bubbles' && (
        <>
          <path
            d="M23 9H33A7 7 0 0 1 40 16V20A7 7 0 0 1 33 27H31.5V31L27 27H23A7 7 0 0 1 16 20V16A7 7 0 0 1 23 9Z"
            fill="#fff"
            fillOpacity="0.5"
          />
          <path
            d="M15 18H27A7.5 7.5 0 0 1 34.5 25.5V29.5A7.5 7.5 0 0 1 27 37H19.5L13.5 41.5V36.8A7.5 7.5 0 0 1 7.5 29.5V25.5A7.5 7.5 0 0 1 15 18Z"
            fill="#fff"
          />
          <circle cx="15.5" cy="27.5" r="1.9" fill="#FC4305" />
          <circle cx="21" cy="27.5" r="1.9" fill="#FC4305" />
          <circle cx="26.5" cy="27.5" r="1.9" fill="#FC4305" />
        </>
      )}

      {mark === 'bridge' && (
        <g stroke="#fff" strokeLinecap="round" strokeLinejoin="round">
          <path d="M10.5 30.5C10.5 12 37.5 12 37.5 30.5" strokeWidth="3.4" />
          <path d="M17.5 19.6V30.5M24 16.7V30.5M30.5 19.6V30.5" strokeWidth="2.4" />
          <path d="M7.5 30.5H40.5" strokeWidth="3.4" />
          <path d="M13 30.5V37M35 30.5V37" strokeWidth="3.4" />
        </g>
      )}
    </svg>
  )
}

/** The first mark — lightning bolt on a flat brand tile. */
function BoltMark({ size }: { size: number }) {
  return (
    <span
      className="flex shrink-0 items-center justify-center rounded-xl bg-brand text-white"
      style={{ width: size, height: size }}
    >
      <Zap style={{ width: size * 0.5, height: size * 0.5 }} strokeWidth={2.5} />
    </span>
  )
}

/**
 * The Setu brand lockup — one source for the sidebar, the sign-in panel and
 * the auth forms. Swap the mark here when a real logo asset is available.
 */
export function BrandLogo({
  variant = 'full',
  size = 40,
  strapline = true,
  mark = BRAND_MARK,
  tone = 'auto',
  className,
}: BrandLogoProps) {
  if (mark === 'image') {
    return (
      <span className={cn('inline-flex items-center', className)}>
        <ImageLogo variant={variant} size={size} tone={tone} />
      </span>
    )
  }
  return (
    <span className={cn('inline-flex items-center gap-3', className)}>
      {mark === 'bolt' ? <BoltMark size={size} /> : <SvgMark size={size} mark={mark} />}
      {variant === 'full' && (
        <span className="min-w-0 text-left">
          <span
            className="heading-text block font-extrabold leading-none tracking-tight"
            style={{ fontSize: size * 0.56 }}
          >
            Setu
          </span>
          {strapline && (
            <span
              className="mt-1 block font-semibold uppercase leading-none tracking-[0.14em] text-clay-400"
              style={{ fontSize: Math.max(9, size * 0.2) }}
            >
              WhatsApp CRM
            </span>
          )}
        </span>
      )}
    </span>
  )
}
